import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumberForOrderId, OrderNumberSchema } from "./order-number.js";
import {
  isTrackedSaleInventoryReturnSnapshot,
  SaleReturnInventoryEffectSchema,
  SaleReturnPhysicalDispositionSchema,
  saleReturnInventoryEffectForDisposition,
  TrackedSaleInventoryReturnSnapshotSchema,
} from "./sale-inventory.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const SafeMoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveMoneyFenSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const SafeQuantitySchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveQuantitySchema = Type.Integer({ minimum: 1, maximum: 99 });
const CanonicalDateTimeSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern:
    "^[0-9]{4}-(0[1-9]|1[0-2])-([0-2][0-9]|3[0-1])T([0-1][0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]\\.[0-9]{3}Z$",
});

export const PaidSaleReturnPaymentMethodSchema = Type.Union([
  Type.Literal("CASH"),
  Type.Literal("STATIC_WECHAT"),
  Type.Literal("STATIC_ALIPAY"),
  Type.Literal("MEMBER_BALANCE"),
  Type.Literal("WECHAT_PAY"),
  Type.Literal("ALIPAY"),
]);
export type PaidSaleReturnPaymentMethod = Static<typeof PaidSaleReturnPaymentMethodSchema>;

export const PaidSaleReturnRefundExecutionModeSchema = Type.Union([
  Type.Literal("LOCAL_CASH_CONFIRMATION"),
  Type.Literal("MANUAL_STATIC_CODE_CONFIRMATION"),
  Type.Literal("MEMBER_BALANCE_CLOUD_REFUND"),
  Type.Literal("OFFICIAL_PROVIDER_REFUND"),
]);
export type PaidSaleReturnRefundExecutionMode = Static<
  typeof PaidSaleReturnRefundExecutionModeSchema
>;

export const PaidSaleReturnPaymentStatusSchema = Type.Union([
  Type.Literal("CONFIRMED_LOCAL"),
  Type.Literal("MANUALLY_RECONCILED"),
  Type.Literal("CONFIRMED_CLOUD"),
  Type.Literal("PARTIALLY_REFUNDED"),
]);
export type PaidSaleReturnPaymentStatus = Static<typeof PaidSaleReturnPaymentStatusSchema>;

export const PaidOrderItemReturnCandidateSchema = Type.Object(
  {
    itemKind: Type.Optional(Type.Union([Type.Literal("PRODUCT"), Type.Literal("PACKAGE")])),
    itemId: StableIdSchema,
    lineNumber: Type.Optional(Type.String({ minLength: 1, maxLength: 80 })),
    componentNames: Type.Optional(
      Type.Array(Type.String({ minLength: 1, maxLength: 80 }), { minItems: 1, maxItems: 30 }),
    ),
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    soldQuantity: PositiveQuantitySchema,
    returnedQuantity: SafeQuantitySchema,
    returnableQuantity: SafeQuantitySchema,
    unitSalePriceFen: PositiveMoneyFenSchema,
    maximumRefundAmountFen: SafeMoneyFenSchema,
    canReturn: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type PaidOrderItemReturnCandidate = Static<typeof PaidOrderItemReturnCandidateSchema>;

export const PaidOrderReturnCandidateSchema = Type.Object(
  {
    orderId: StableIdSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    orderStatus: Type.Union([Type.Literal("SETTLED"), Type.Literal("REFUNDED")]),
    originalPaidFen: PositiveMoneyFenSchema,
    alreadyRefundedFen: SafeMoneyFenSchema,
    paymentMethod: Type.Union([PaidSaleReturnPaymentMethodSchema, Type.Null()]),
    refundExecutionMode: Type.Union([PaidSaleReturnRefundExecutionModeSchema, Type.Null()]),
    paymentMethodLabel: Type.String({ minLength: 2, maxLength: 80 }),
    paymentSourceCount: Type.Optional(Type.Integer({ minimum: 1, maximum: 32 })),
    items: Type.Array(PaidOrderItemReturnCandidateSchema, { minItems: 1, maxItems: 200 }),
    canStartReturn: Type.Boolean(),
    blocker: Type.Union([Type.String({ minLength: 2, maxLength: 300 }), Type.Null()]),
  },
  { additionalProperties: false },
);
export type PaidOrderReturnCandidate = Static<typeof PaidOrderReturnCandidateSchema>;

export const PaidOrderReturnCandidateListReadModelSchema = Type.Object(
  {
    orders: Type.Array(PaidOrderReturnCandidateSchema, { maxItems: 100 }),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type PaidOrderReturnCandidateListReadModel = Static<
  typeof PaidOrderReturnCandidateListReadModelSchema
>;

function isPaidOrderItemReturnCandidate(value: PaidOrderItemReturnCandidate): boolean {
  const expectedReturnable = value.soldQuantity - value.returnedQuantity;
  const expectedMaximumRefund = value.unitSalePriceFen * value.returnableQuantity;
  return (
    Number.isSafeInteger(expectedReturnable) &&
    expectedReturnable >= 0 &&
    value.returnableQuantity === expectedReturnable &&
    Number.isSafeInteger(expectedMaximumRefund) &&
    value.maximumRefundAmountFen === expectedMaximumRefund &&
    (value.canReturn
      ? value.blocker === null && value.returnableQuantity > 0
      : value.blocker !== null)
  );
}

export function isPaidOrderReturnCandidateListReadModel(
  value: unknown,
): value is PaidOrderReturnCandidateListReadModel {
  if (!Value.Check(PaidOrderReturnCandidateListReadModelSchema, value)) return false;
  return value.orders.every(
    (order) =>
      isOrderNumberForOrderId(order.orderNumber, order.orderId, order.businessDate) &&
      order.alreadyRefundedFen <= order.originalPaidFen &&
      order.items.every(isPaidOrderItemReturnCandidate) &&
      (order.canStartReturn
        ? order.blocker === null &&
          ((order.paymentSourceCount ?? 1) > 1
            ? order.paymentMethod === null && order.refundExecutionMode === null
            : order.paymentMethod !== null && order.refundExecutionMode !== null) &&
          order.items.some((item) => item.canReturn)
        : order.blocker !== null),
  );
}

export const PaidSaleReturnOriginalPaymentSchema = Type.Object(
  {
    paymentId: StableIdSchema,
    method: PaidSaleReturnPaymentMethodSchema,
    status: PaidSaleReturnPaymentStatusSchema,
    paymentAmountFen: PositiveMoneyFenSchema,
    alreadyRefundedFen: SafeMoneyFenSchema,
    availableFen: PositiveMoneyFenSchema,
    refundAmountFen: PositiveMoneyFenSchema,
    executionMode: PaidSaleReturnRefundExecutionModeSchema,
    memberId: Type.Union([StableIdSchema, Type.Null()]),
    memberTransactionId: Type.Union([StableIdSchema, Type.Null()]),
    officialPaymentAttemptId: Type.Union([StableIdSchema, Type.Null()]),
    providerCode: Type.Union([
      Type.String({ minLength: 2, maxLength: 32, pattern: "^[A-Z][A-Z0-9_]{1,31}$" }),
      Type.Null(),
    ]),
    providerTransactionReference: Type.Union([StableIdSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type PaidSaleReturnOriginalPayment = Static<typeof PaidSaleReturnOriginalPaymentSchema>;

export const PaidSaleReturnOriginalPaymentsSchema = Type.Array(
  PaidSaleReturnOriginalPaymentSchema,
  { minItems: 1, maxItems: 32 },
);
export type PaidSaleReturnOriginalPayments = Static<typeof PaidSaleReturnOriginalPaymentsSchema>;

export interface PaidSaleReturnAllocationSource {
  paymentId: string;
  paymentAmountFen: number;
  alreadyRefundedFen: number;
}

export interface PaidSaleReturnAllocationResult {
  paymentId: string;
  refundAmountFen: number;
}

function cumulativeProportionalRefundTargets(
  sources: readonly PaidSaleReturnAllocationSource[],
  totalPaidFen: number,
  cumulativeRefundFen: number,
): number[] | null {
  if (
    sources.length < 1 ||
    sources.length > 32 ||
    !Number.isSafeInteger(totalPaidFen) ||
    totalPaidFen <= 0 ||
    !Number.isSafeInteger(cumulativeRefundFen) ||
    cumulativeRefundFen < 0 ||
    cumulativeRefundFen > totalPaidFen
  ) {
    return null;
  }
  const denominator = BigInt(totalPaidFen);
  const weighted = sources.map((source, index) => {
    const numerator = BigInt(cumulativeRefundFen) * BigInt(source.paymentAmountFen);
    return {
      index,
      paymentId: source.paymentId,
      quotient: Number(numerator / denominator),
      remainder: numerator % denominator,
    };
  });
  const floorTotal = weighted.reduce((sum, item) => sum + item.quotient, 0);
  const remaining = cumulativeRefundFen - floorTotal;
  if (!Number.isSafeInteger(remaining) || remaining < 0 || remaining >= sources.length) return null;
  weighted.sort((left, right) => {
    if (left.remainder !== right.remainder) return left.remainder > right.remainder ? -1 : 1;
    if (left.index !== right.index) return left.index - right.index;
    return left.paymentId.localeCompare(right.paymentId);
  });
  for (let index = 0; index < remaining; index += 1) {
    const target = weighted[index];
    if (!target) return null;
    target.quotient += 1;
  }
  return weighted.sort((left, right) => left.index - right.index).map((item) => item.quotient);
}

export function allocatePaidSaleReturnAcrossPayments(
  sources: readonly PaidSaleReturnAllocationSource[],
  cumulativeRefundBeforeFen: number,
  refundAmountFen: number,
): PaidSaleReturnAllocationResult[] | null {
  if (
    !Number.isSafeInteger(refundAmountFen) ||
    refundAmountFen <= 0 ||
    new Set(sources.map((source) => source.paymentId)).size !== sources.length ||
    sources.some(
      (source) =>
        source.paymentId.length < 1 ||
        !Number.isSafeInteger(source.paymentAmountFen) ||
        source.paymentAmountFen <= 0 ||
        !Number.isSafeInteger(source.alreadyRefundedFen) ||
        source.alreadyRefundedFen < 0 ||
        source.alreadyRefundedFen > source.paymentAmountFen,
    )
  ) {
    return null;
  }
  const totalPaidFen = sources.reduce((sum, source) => sum + source.paymentAmountFen, 0);
  const cumulativeRefundAfterFen = cumulativeRefundBeforeFen + refundAmountFen;
  if (
    !Number.isSafeInteger(totalPaidFen) ||
    !Number.isSafeInteger(cumulativeRefundAfterFen) ||
    cumulativeRefundAfterFen > totalPaidFen
  ) {
    return null;
  }
  const beforeTargets = cumulativeProportionalRefundTargets(
    sources,
    totalPaidFen,
    cumulativeRefundBeforeFen,
  );
  const afterTargets = cumulativeProportionalRefundTargets(
    sources,
    totalPaidFen,
    cumulativeRefundAfterFen,
  );
  if (
    !beforeTargets ||
    !afterTargets ||
    sources.some((source, index) => source.alreadyRefundedFen !== beforeTargets[index])
  ) {
    return null;
  }
  const allocations = sources.flatMap((source, index) => {
    const before = beforeTargets[index];
    const after = afterTargets[index];
    if (before === undefined || after === undefined || after < before) return [];
    const amount = after - before;
    return amount > 0 ? [{ paymentId: source.paymentId, refundAmountFen: amount }] : [];
  });
  return allocations.reduce((sum, item) => sum + item.refundAmountFen, 0) === refundAmountFen
    ? allocations
    : null;
}

function nullableStringPresent(value: string | null): value is string {
  return typeof value === "string" && value.length > 0;
}

export function isPaidSaleReturnOriginalPayment(
  value: unknown,
): value is PaidSaleReturnOriginalPayment {
  if (!Value.Check(PaidSaleReturnOriginalPaymentSchema, value)) return false;
  const payment = value as PaidSaleReturnOriginalPayment;
  if (
    payment.availableFen !== payment.paymentAmountFen - payment.alreadyRefundedFen ||
    payment.refundAmountFen > payment.availableFen
  ) {
    return false;
  }

  const hasMemberBinding =
    nullableStringPresent(payment.memberId) && nullableStringPresent(payment.memberTransactionId);
  const hasOfficialBinding =
    nullableStringPresent(payment.officialPaymentAttemptId) &&
    nullableStringPresent(payment.providerCode) &&
    nullableStringPresent(payment.providerTransactionReference);
  const noMemberBinding = payment.memberId === null && payment.memberTransactionId === null;
  const noOfficialBinding =
    payment.officialPaymentAttemptId === null &&
    payment.providerCode === null &&
    payment.providerTransactionReference === null;

  switch (payment.method) {
    case "CASH":
      return (
        payment.executionMode === "LOCAL_CASH_CONFIRMATION" &&
        (payment.status === "CONFIRMED_LOCAL" ||
          payment.status === "MANUALLY_RECONCILED" ||
          payment.status === "PARTIALLY_REFUNDED") &&
        noMemberBinding &&
        noOfficialBinding
      );
    case "STATIC_WECHAT":
    case "STATIC_ALIPAY":
      return (
        payment.executionMode === "MANUAL_STATIC_CODE_CONFIRMATION" &&
        (payment.status === "MANUALLY_RECONCILED" || payment.status === "PARTIALLY_REFUNDED") &&
        noMemberBinding &&
        noOfficialBinding
      );
    case "MEMBER_BALANCE":
      return (
        payment.executionMode === "MEMBER_BALANCE_CLOUD_REFUND" &&
        (payment.status === "CONFIRMED_CLOUD" || payment.status === "PARTIALLY_REFUNDED") &&
        hasMemberBinding &&
        noOfficialBinding
      );
    case "WECHAT_PAY":
    case "ALIPAY":
      return (
        payment.executionMode === "OFFICIAL_PROVIDER_REFUND" &&
        (payment.status === "CONFIRMED_CLOUD" || payment.status === "PARTIALLY_REFUNDED") &&
        noMemberBinding &&
        hasOfficialBinding
      );
  }
}

const PaidOrderItemSaleReturnApprovalBindingV1Schema = Type.Object(
  {
    bindingVersion: Type.Literal(1),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    itemId: StableIdSchema,
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    quantity: PositiveQuantitySchema,
    returnedQuantityBefore: SafeQuantitySchema,
    returnedQuantityAfter: SafeQuantitySchema,
    orderAmountReductionFen: PositiveMoneyFenSchema,
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    paymentResolution: Type.Literal("ORIGINAL_PAYMENT_PARTIAL_REFUND"),
    originalPayment: PaidSaleReturnOriginalPaymentSchema,
    originalSaleCostSnapshotSha256: Sha256Schema,
    inventoryFactSha256: Type.Union([Sha256Schema, Type.Null()]),
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    capturedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);

export const PaidOrderItemSaleReturnApprovalBindingV2Schema = Type.Object(
  {
    bindingVersion: Type.Literal(2),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    itemId: StableIdSchema,
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    quantity: PositiveQuantitySchema,
    returnedQuantityBefore: SafeQuantitySchema,
    returnedQuantityAfter: SafeQuantitySchema,
    orderAmountReductionFen: PositiveMoneyFenSchema,
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    paymentResolution: Type.Literal("ORIGINAL_PAYMENT_PARTIAL_REFUND"),
    originalPayments: PaidSaleReturnOriginalPaymentsSchema,
    originalSaleCostSnapshotSha256: Sha256Schema,
    inventoryFactSha256: Type.Union([Sha256Schema, Type.Null()]),
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    capturedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);

export const PaidStorePackageReturnApprovalBindingV3Schema = Type.Object(
  {
    bindingVersion: Type.Literal(3),
    itemKind: Type.Literal("PACKAGE"),
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    itemId: StableIdSchema,
    packageLineNumber: Type.String({ pattern: "^TC-[0-9]{8}-[0-9A-F]{12}$" }),
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    quantity: Type.Literal(1),
    returnedQuantityBefore: Type.Literal(0),
    returnedQuantityAfter: Type.Literal(1),
    orderAmountReductionFen: PositiveMoneyFenSchema,
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    paymentResolution: Type.Literal("ORIGINAL_PAYMENT_PARTIAL_REFUND"),
    originalPayments: PaidSaleReturnOriginalPaymentsSchema,
    originalSaleCostSnapshotSha256: Sha256Schema,
    inventoryFactSha256: Type.Union([Sha256Schema, Type.Null()]),
    sourceReferenceSha256: Sha256Schema,
    businessFactSha256: Sha256Schema,
    capturedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);

export const PaidOrderItemSaleReturnApprovalBindingSchema = Type.Union([
  PaidOrderItemSaleReturnApprovalBindingV1Schema,
  PaidOrderItemSaleReturnApprovalBindingV2Schema,
  PaidStorePackageReturnApprovalBindingV3Schema,
]);
export type PaidOrderItemSaleReturnApprovalBinding = Static<
  typeof PaidOrderItemSaleReturnApprovalBindingSchema
>;

export function paidSaleReturnOriginalPayments(
  binding: PaidOrderItemSaleReturnApprovalBinding,
): readonly PaidSaleReturnOriginalPayment[] {
  return binding.bindingVersion === 1 ? [binding.originalPayment] : binding.originalPayments;
}

function isPaidSaleReturnOriginalPaymentPlan(
  payments: readonly PaidSaleReturnOriginalPayment[],
  totalRefundFen: number,
): boolean {
  const paymentIds = new Set<string>();
  let allocatedFen = 0;
  for (const payment of payments) {
    if (
      !isPaidSaleReturnOriginalPayment(payment) ||
      paymentIds.has(payment.paymentId) ||
      !Number.isSafeInteger(allocatedFen + payment.refundAmountFen)
    ) {
      return false;
    }
    paymentIds.add(payment.paymentId);
    allocatedFen += payment.refundAmountFen;
  }
  return allocatedFen === totalRefundFen;
}

export function isPaidOrderItemSaleReturnApprovalBinding(
  value: unknown,
): value is PaidOrderItemSaleReturnApprovalBinding {
  if (!Value.Check(PaidOrderItemSaleReturnApprovalBindingSchema, value)) return false;
  const binding = value as PaidOrderItemSaleReturnApprovalBinding;
  const originalPayments = paidSaleReturnOriginalPayments(binding);
  if (binding.bindingVersion === 3) {
    return (
      isOrderNumberForOrderId(binding.orderNumber, binding.orderId, binding.businessDate) &&
      binding.quantity === 1 &&
      binding.returnedQuantityBefore === 0 &&
      binding.returnedQuantityAfter === 1 &&
      binding.inventoryEffect ===
        saleReturnInventoryEffectForDisposition(binding.physicalDisposition) &&
      (binding.physicalDisposition === "SALEABLE_RETURNED"
        ? binding.inventoryFactSha256 !== null
        : binding.inventoryFactSha256 === null) &&
      isPaidSaleReturnOriginalPaymentPlan(originalPayments, binding.orderAmountReductionFen)
    );
  }
  return (
    isOrderNumberForOrderId(binding.orderNumber, binding.orderId, binding.businessDate) &&
    binding.returnedQuantityAfter === binding.returnedQuantityBefore + binding.quantity &&
    binding.inventoryEffect ===
      saleReturnInventoryEffectForDisposition(binding.physicalDisposition) &&
    (binding.physicalDisposition === "SALEABLE_RETURNED"
      ? binding.inventoryFactSha256 !== null
      : binding.inventoryFactSha256 === null) &&
    isPaidSaleReturnOriginalPaymentPlan(originalPayments, binding.orderAmountReductionFen)
  );
}

export const PaidSaleReturnRefundEvidenceSchema = Type.Object(
  {
    refundId: StableIdSchema,
    originalPaymentId: StableIdSchema,
    method: PaidSaleReturnPaymentMethodSchema,
    amountFen: PositiveMoneyFenSchema,
    evidence: Type.Union([
      Type.Literal("STORE_ACCEPTED"),
      Type.Literal("MANUAL_FALLBACK"),
      Type.Literal("PROVIDER_ACCEPTED"),
    ]),
    externalReference: Type.Union([StableIdSchema, Type.Null()]),
    memberRefundTransactionId: Type.Union([StableIdSchema, Type.Null()]),
    providerRefundReference: Type.Union([StableIdSchema, Type.Null()]),
    confirmedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type PaidSaleReturnRefundEvidence = Static<typeof PaidSaleReturnRefundEvidenceSchema>;

export const PaidSaleReturnRefundEvidencesSchema = Type.Array(PaidSaleReturnRefundEvidenceSchema, {
  minItems: 1,
  maxItems: 32,
});
export type PaidSaleReturnRefundEvidences = Static<typeof PaidSaleReturnRefundEvidencesSchema>;

export function isPaidSaleReturnRefundEvidence(
  value: unknown,
): value is PaidSaleReturnRefundEvidence {
  if (!Value.Check(PaidSaleReturnRefundEvidenceSchema, value)) return false;
  const evidence = value as PaidSaleReturnRefundEvidence;
  switch (evidence.method) {
    case "CASH":
      return (
        evidence.evidence === "STORE_ACCEPTED" &&
        evidence.externalReference === null &&
        evidence.memberRefundTransactionId === null &&
        evidence.providerRefundReference === null
      );
    case "STATIC_WECHAT":
    case "STATIC_ALIPAY":
      return (
        evidence.evidence === "MANUAL_FALLBACK" &&
        nullableStringPresent(evidence.externalReference) &&
        evidence.memberRefundTransactionId === null &&
        evidence.providerRefundReference === null
      );
    case "MEMBER_BALANCE":
      return (
        evidence.evidence === "PROVIDER_ACCEPTED" &&
        evidence.externalReference === null &&
        nullableStringPresent(evidence.memberRefundTransactionId) &&
        evidence.providerRefundReference === null
      );
    case "WECHAT_PAY":
    case "ALIPAY":
      return (
        evidence.evidence === "PROVIDER_ACCEPTED" &&
        evidence.externalReference === null &&
        evidence.memberRefundTransactionId === null &&
        nullableStringPresent(evidence.providerRefundReference)
      );
  }
}

const PaidOrderItemSaleReturnRecordV1Schema = Type.Object(
  {
    kind: Type.Literal("ORDER_ITEM_PAID_SALE_RETURN"),
    returnId: StableIdSchema,
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    itemId: StableIdSchema,
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    quantity: PositiveQuantitySchema,
    returnedQuantityBefore: SafeQuantitySchema,
    returnedQuantityAfter: SafeQuantitySchema,
    orderAmountReductionFen: PositiveMoneyFenSchema,
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    paymentResolution: Type.Literal("ORIGINAL_PAYMENT_PARTIAL_REFUND"),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    approvalId: StableIdSchema,
    approvalSnapshotSha256: Sha256Schema,
    originalPayment: PaidSaleReturnOriginalPaymentSchema,
    refundEvidence: PaidSaleReturnRefundEvidenceSchema,
    inventoryReturnSnapshot: Type.Union([TrackedSaleInventoryReturnSnapshotSchema, Type.Null()]),
    occurredAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);

export const PaidOrderItemSaleReturnRecordV2Schema = Type.Object(
  {
    kind: Type.Literal("ORDER_ITEM_PAID_SALE_RETURN"),
    recordVersion: Type.Literal(2),
    returnId: StableIdSchema,
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: OrderNumberSchema,
    businessDate: BusinessDateSchema,
    itemId: StableIdSchema,
    skuId: StableIdSchema,
    productName: Type.String({ minLength: 1, maxLength: 200 }),
    quantity: PositiveQuantitySchema,
    returnedQuantityBefore: SafeQuantitySchema,
    returnedQuantityAfter: SafeQuantitySchema,
    orderAmountReductionFen: PositiveMoneyFenSchema,
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    paymentResolution: Type.Literal("ORIGINAL_PAYMENT_PARTIAL_REFUND"),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    approvalId: StableIdSchema,
    approvalSnapshotSha256: Sha256Schema,
    originalPayments: PaidSaleReturnOriginalPaymentsSchema,
    refundEvidences: PaidSaleReturnRefundEvidencesSchema,
    inventoryReturnSnapshot: Type.Union([TrackedSaleInventoryReturnSnapshotSchema, Type.Null()]),
    occurredAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);

export const PaidOrderItemSaleReturnRecordSchema = Type.Union([
  PaidOrderItemSaleReturnRecordV1Schema,
  PaidOrderItemSaleReturnRecordV2Schema,
]);
export type PaidOrderItemSaleReturnRecord = Static<typeof PaidOrderItemSaleReturnRecordSchema>;

export function paidSaleReturnRecordOriginalPayments(
  record: PaidOrderItemSaleReturnRecord,
): readonly PaidSaleReturnOriginalPayment[] {
  return "recordVersion" in record ? record.originalPayments : [record.originalPayment];
}

export function paidSaleReturnRecordRefundEvidences(
  record: PaidOrderItemSaleReturnRecord,
): readonly PaidSaleReturnRefundEvidence[] {
  return "recordVersion" in record ? record.refundEvidences : [record.refundEvidence];
}

export function isPaidOrderItemSaleReturnRecord(
  value: unknown,
): value is PaidOrderItemSaleReturnRecord {
  if (!Value.Check(PaidOrderItemSaleReturnRecordSchema, value)) return false;
  const record = value as PaidOrderItemSaleReturnRecord;
  const originalPayments = paidSaleReturnRecordOriginalPayments(record);
  const refundEvidences = paidSaleReturnRecordRefundEvidences(record);
  const paymentById = new Map(originalPayments.map((payment) => [payment.paymentId, payment]));
  const evidenceIds = new Set<string>();
  const evidencedPaymentIds = new Set<string>();
  let refundedFen = 0;
  return (
    isOrderNumberForOrderId(record.orderNumber, record.orderId, record.businessDate) &&
    record.returnedQuantityAfter === record.returnedQuantityBefore + record.quantity &&
    isPaidSaleReturnOriginalPaymentPlan(originalPayments, record.orderAmountReductionFen) &&
    refundEvidences.length === originalPayments.length &&
    refundEvidences.every((evidence) => {
      const payment = paymentById.get(evidence.originalPaymentId);
      if (
        !payment ||
        evidenceIds.has(evidence.refundId) ||
        evidencedPaymentIds.has(evidence.originalPaymentId) ||
        !isPaidSaleReturnRefundEvidence(evidence) ||
        evidence.method !== payment.method ||
        evidence.amountFen !== payment.refundAmountFen ||
        !Number.isSafeInteger(refundedFen + evidence.amountFen)
      ) {
        return false;
      }
      evidenceIds.add(evidence.refundId);
      evidencedPaymentIds.add(evidence.originalPaymentId);
      refundedFen += evidence.amountFen;
      return true;
    }) &&
    refundedFen === record.orderAmountReductionFen &&
    record.inventoryEffect ===
      saleReturnInventoryEffectForDisposition(record.physicalDisposition) &&
    (record.physicalDisposition === "SALEABLE_RETURNED"
      ? isTrackedSaleInventoryReturnSnapshot(record.inventoryReturnSnapshot) &&
        record.inventoryReturnSnapshot.skuId === record.skuId &&
        record.inventoryReturnSnapshot.returnedBaseQuantity === record.quantity &&
        record.inventoryReturnSnapshot.returnedQuantityBefore === record.returnedQuantityBefore &&
        record.inventoryReturnSnapshot.returnedQuantityAfter === record.returnedQuantityAfter
      : record.inventoryReturnSnapshot === null)
  );
}

export const PaidOrderItemSaleReturnOperatorResultSchema = Type.Object(
  {
    returnId: StableIdSchema,
    itemId: StableIdSchema,
    quantity: PositiveQuantitySchema,
    orderAmountReductionFen: PositiveMoneyFenSchema,
    refundId: StableIdSchema,
    refundMethod: PaidSaleReturnPaymentMethodSchema,
    refundAmountFen: PositiveMoneyFenSchema,
    orderStatus: Type.Union([Type.Literal("SETTLED"), Type.Literal("REFUNDED")]),
    returnedQuantityAfter: SafeQuantitySchema,
    physicalDisposition: SaleReturnPhysicalDispositionSchema,
    inventoryEffect: SaleReturnInventoryEffectSchema,
    occurredAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type PaidOrderItemSaleReturnOperatorResult = Static<
  typeof PaidOrderItemSaleReturnOperatorResultSchema
>;

export function isPaidOrderItemSaleReturnOperatorResult(
  value: unknown,
): value is PaidOrderItemSaleReturnOperatorResult {
  return (
    Value.Check(PaidOrderItemSaleReturnOperatorResultSchema, value) &&
    (value as PaidOrderItemSaleReturnOperatorResult).orderAmountReductionFen ===
      (value as PaidOrderItemSaleReturnOperatorResult).refundAmountFen &&
    (value as PaidOrderItemSaleReturnOperatorResult).inventoryEffect ===
      saleReturnInventoryEffectForDisposition(
        (value as PaidOrderItemSaleReturnOperatorResult).physicalDisposition,
      )
  );
}
